import { useState, useCallback } from "react";
import CreateModal, { CreateModalProps } from "../index";
import MultiStepGradeModal, {
  MultiStepGradeModalProps,
} from "./MultiStepGradeModal";
import BasicAdvancedToggle, { CreationMode } from "./BasicAdvancedToggle";
import { ThemeColor } from "../OutputLanguageSelector";

export interface BasicAdvancedWrapperProps<T = any> {
  // Basic mode props
  createModalProps: CreateModalProps;

  // Advanced mode props
  multiStepProps: Omit<MultiStepGradeModalProps<T>, "createModalProps">;

  // Common props
  themeColor: ThemeColor;
  title: string;
  backPath: string;

  // Configuration
  enableAdvancedMode?: boolean;
  defaultMode?: CreationMode;
  showModeToggle?: boolean;

  // Callbacks
  onBasicSubmit: (data: any) => Promise<void>;
  onAdvancedSubmit: (data: T) => Promise<void>;
}

const BasicAdvancedWrapper = <T extends Record<string, any>>({
  createModalProps,
  multiStepProps,
  themeColor,
  title,
  backPath,
  enableAdvancedMode = true,
  defaultMode = "basic",
  showModeToggle = true,
  onBasicSubmit,
  onAdvancedSubmit,
}: BasicAdvancedWrapperProps<T>) => {
  const [creationMode, setCreationMode] = useState<CreationMode>(defaultMode);

  const handleModeChange = useCallback((mode: CreationMode) => {
    setCreationMode(mode);
  }, []);

  // Prepare CreateModal props for both modes
  const basicCreateModalProps: CreateModalProps = {
    ...createModalProps,
    onSubmit: onBasicSubmit,
  };

  const advancedCreateModalProps: CreateModalProps = {
    ...createModalProps,
    // Remove onSubmit as it will be handled by MultiStepGradeModal
    onSubmit: async () => {}, // Placeholder, will be overridden
  };

  // If advanced mode is disabled, always show basic mode
  if (!enableAdvancedMode) {
    return <CreateModal {...basicCreateModalProps} />;
  }

  return (
    <>
      {/* Mode toggle */}
      {showModeToggle && (
        <div className="absolute top-4 left-1/2 z-50">
          <BasicAdvancedToggle
            mode={creationMode}
            onModeChange={handleModeChange}
            themeColor={themeColor}
            disabled={createModalProps.isLoading}
          />
        </div>
      )}

      {/* Render appropriate modal based on mode */}
      {creationMode === "basic" ? (
        <CreateModal {...basicCreateModalProps} />
      ) : (
        <MultiStepGradeModal
          {...multiStepProps}
          createModalProps={advancedCreateModalProps}
          themeColor={themeColor}
          title={title}
          backPath={backPath}
          onFinalSubmit={onAdvancedSubmit}
        />
      )}
    </>
  );
};

export default BasicAdvancedWrapper;
